import {
  ActionConfirmationModal,
  Button,
  LoadingSpinner,
  SheetFooter,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { useUser } from "@/components/UserProvider";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { PDF_MIME_TYPE, PdfEditor, type PdfEditorHandle } from "./PdfEditor";
import { useFileEditor } from "@/lib/data/react-query/files/use-file-editor";
import { fileOperationMessage } from "@/lib/data/requests/files/canonical-files";
import { VITE_APRYSE_LICENSE_KEY } from "@/lib/data/static/importEnv";
import type { MatterFile } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import { useEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import toast from "react-hot-toast";

interface EditPdfFileModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId: string;
  sourceFile: MatterFile;
  onSaved?: () => void;
  showSaveAsCopyButton?: boolean;
}

export const EditPdfFileModal = ({
  open,
  setOpen,
  sourceFile,
  onSaved,
  showSaveAsCopyButton = true,
}: EditPdfFileModalProps) => {
  const { currentUser } = useUser();
  const annotationUserName = formatUsersName(currentUser)?.trim();
  const editorFile = useFileEditor(sourceFile.id, open);
  const editorRef = useRef<PdfEditorHandle | null>(null);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasAppliedRedactions, setHasAppliedRedactions] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [saveMode, setSaveMode] = useState<"copy" | "original" | null>(null);

  useEffect(() => {
    if (!open) {
      setIsEditorReady(false);
      setIsExporting(false);
      setHasAppliedRedactions(false);
      setConfirmClose(false);
      setSaveMode(null);
      return;
    }
  }, [open, sourceFile.name]);

  const handleCreateFromPdf = async () => {
    if (!editorRef.current) {
      toast.error("PDF editor is not ready yet.");
      return;
    }

    setSaveMode("copy");
    setIsExporting(true);
    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      await editorFile.copy.mutateAsync({
        blob,
        contentType: PDF_MIME_TYPE,
        sourceName: sourceFile.name,
      });

      toast.success("Document saved as a copy.");
      onSaved?.();
      setOpen(false);
    } catch (error) {
      console.error("Failed to save edited PDF", error);
      toast.error(fileOperationMessage(error, "Failed to save document"));
    } finally {
      setIsExporting(false);
      setSaveMode(null);
    }
  };

  const handleSaveOriginal = async () => {
    if (!editorRef.current) {
      toast.error("PDF editor is not ready yet.");
      return;
    }

    setSaveMode("original");
    setIsExporting(true);

    try {
      const blob = await editorRef.current.exportDocument();
      if (!blob) {
        toast.error("Unable to export the document.");
        return;
      }

      await editorFile.save.mutateAsync({ blob, contentType: PDF_MIME_TYPE });

      toast.success("Document saved");
      onSaved?.();
      setOpen(false);
    } catch (error) {
      console.error("Failed to save PDF document", error);
      toast.error(fileOperationMessage(error, "Failed to save document"));
    } finally {
      setIsExporting(false);
      setSaveMode(null);
    }
  };

  const isSaving =
    editorFile.copy.isPending ||
    editorFile.save.isPending ||
    isExporting ||
    saveMode !== null;

  return (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      isDirty
      hideHeader
      hideCloseButton
      overlayClassName="z-[108] bg-black/50 backdrop-blur-md"
      title={`Edit ${sourceFile.name}`}
      description={
        showSaveAsCopyButton
          ? "Edit the PDF or mark redactions, then save changes to the original file or create a new copy."
          : "Edit the PDF or mark redactions, then save changes to the original file."
      }
      bodyContained
      className="top-0 left-0 z-[109] h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none translate-x-0 translate-y-0 rounded-none"
    >
      <div className="relative flex min-h-0 grow flex-col overflow-hidden">
        {isSaving && <FormLoadingOverlay />}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="absolute top-4 right-4 z-10 h-8 w-8 p-0"
          onClick={() => {
            setConfirmClose(true);
          }}
        >
          <XIcon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
        <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
          {editorFile.isLoading ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <LoadingSpinner className="h-6 w-6" />
            </div>
          ) : editorFile.isError ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <Typography className="text-destructive text-sm">
                Unable to load matter data for this PDF.
              </Typography>
            </div>
          ) : (
            <PdfEditor
              key={`pdf-editor-${sourceFile.id}`}
              ref={editorRef}
              fileName={sourceFile.name}
              initialDocUrl={editorFile.url}
              fileType="matter-file"
              annotationUserName={annotationUserName}
              licenseKey={VITE_APRYSE_LICENSE_KEY}
              onReadyChange={setIsEditorReady}
              onAppliedRedactionsChange={setHasAppliedRedactions}
              isActive={open && !!editorFile.url}
              showSaveButton={false}
              showHeader
              className="min-h-0 flex-1"
            />
          )}
        </div>
        {!editorFile.isLoading &&
          !editorFile.isError &&
          !editorFile.canSave && (
            <p className="text-muted-foreground px-6 py-2 text-sm">
              {editorFile.saveUnavailableReason}
            </p>
          )}
        <SheetFooter className="mt-auto shrink-0 border-t px-6 py-4">
          <div className="flex grow items-center justify-between gap-x-2">
            {hasAppliedRedactions ? (
              <Typography className="text-muted-foreground text-sm">
                Redactions were applied. Use save as to create a new file.
              </Typography>
            ) : (
              <div />
            )}
            <div className="flex justify-end gap-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setConfirmClose(true);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant={showSaveAsCopyButton ? "outline" : "light-primary"}
                onClick={() => {
                  void handleSaveOriginal();
                }}
                disabled={
                  !editorFile.canSave ||
                  editorFile.isLoading ||
                  editorFile.isError ||
                  !isEditorReady ||
                  isSaving ||
                  hasAppliedRedactions
                }
              >
                {saveMode === "original" && isSaving ? "Saving..." : "Save"}
              </Button>
              {showSaveAsCopyButton ? (
                <Button
                  type="button"
                  variant="light-primary"
                  onClick={() => {
                    void handleCreateFromPdf();
                  }}
                  disabled={
                    editorFile.isLoading ||
                    editorFile.isError ||
                    !isEditorReady ||
                    isSaving
                  }
                >
                  {saveMode === "copy" && isSaving
                    ? "Saving..."
                    : "Save as copy"}
                </Button>
              ) : null}
            </div>
          </div>
        </SheetFooter>
      </div>
      <ActionConfirmationModal
        open={confirmClose}
        setOpen={setConfirmClose}
        title="Unsaved changes"
        buttonText="Confirm"
        description="You have unsaved changes. Are you sure you want to close?"
        afterActionFn={() => {
          setOpen(false);
          setConfirmClose(false);
        }}
      />
    </AppModal>
  );
};

export const EditPdfFileSlideover = EditPdfFileModal;
